import { Theme } from "../theme.generated";

/**
 * THE PLATE'S HOT STATE. A literal transcription of `RevivalMenuMotion.UpdateHovers` +
 * `RevivalPlate`.
 *
 * 🚨 **EVERY PLATE IS IDENTICAL AND COLD AT REST. THE WARM FILL, THE GOLD RIM AND THE LIFT ARE THE
 * HOVER STATE.** This is written down because it was got wrong once, expensively.
 * `RevivalScreen.Action` records it:
 *
 * > Alex, 2026-08-10: *"The reason PLAY on this page is bigger and in a different colour is because
 * > it is being selected or hovered over... That is a dynamic feature to implement."*
 * > **THE APPROVAL IS A SCREENSHOT OF A LIVE STATE, NOT A STATIC COMPOSITION.** ... Every one of
 * > those is a mis-reading of a HOVER.
 *
 * A first pass of the web menu repeated the identical mistake and shipped PLAY as a permanently
 * warm `plate--primary`. **A mock cannot tell you which of its pixels are a state.**
 *
 * ⭐ **THE NUMBERS ARE MEASURED, NOT CHOSEN**, and the comment in the original says so: the
 * approval's rest plates are 340x57 (aspect 6.00) and its selected plate is 420x85 (4.94), so the
 * hover grows MORE IN HEIGHT than in width. That is why the scale is NON-UNIFORM - x1.235, y1.518 -
 * and why a single factor never resembled the mock.
 *
 * ⚠️ **ONE OWNER FOR "IS THIS CONTROL HOT."** Colour, rim and scale are driven off the SAME test in
 * the SAME frame. Two hover detectors drift, and you get a warm plate at rest size on exactly the
 * frames a player is looking at it.
 *
 * ⚠️ **KEYBOARD SELECTION COUNTS AS HOT, NOT ONLY THE POINTER.** The original tracks
 * `EventSystem.currentSelectedGameObject`; here it is `:focus-visible`. A menu that only responds to
 * a mouse is one a keyboard or controller player cannot read at all.
 */

const HOT_SCALE_X = 1.235;
const HOT_SCALE_Y = 1.518;
const HOT_EDGE_WIDTH = 0.014; // fraction of the plate's height
const SMOOTHING = 12; // k = 1 - exp(-SMOOTHING * dt)

interface Rgba {
  r: number;
  g: number;
  b: number;
  a: number;
}

/** `#RGB`, `#RRGGBB` and `#RRGGBBAA` - the palette uses the 8-digit form for Panel and Scrim. */
function parseHex(hex: string): Rgba {
  let h = hex.replace("#", "").trim();
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  const a = h.length >= 8 ? parseInt(h.slice(6, 8), 16) / 255 : 1;
  return { r, g, b, a };
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function mix(a: Rgba, b: Rgba, t: number): string {
  const r = Math.round(lerp(a.r, b.r, t));
  const g = Math.round(lerp(a.g, b.g, t));
  const bl = Math.round(lerp(a.b, b.b, t));
  const al = lerp(a.a, b.a, t);
  return `rgba(${r}, ${g}, ${bl}, ${al.toFixed(3)})`;
}

const REST_FILL = parseHex(Theme.SecondaryFill);
const HOT_FILL = parseHex(Theme.PrimaryFill);
const REST_LABEL = parseHex(Theme.Display);
// `PlateEdge(Primary)` is `Action`; at rest the menu's plates carry NO rim, so the rest edge is the
// same colour at zero alpha rather than a different hue fading in.
const HOT_EDGE = parseHex(Theme.Action);
const REST_EDGE: Rgba = { ...HOT_EDGE, a: 0 };

export interface PlateAnimator {
  stop(): void;
}

/**
 * Drive the hot state for every `.plate` under `root`.
 *
 * ⚠️ ONE rAF LOOP FOR ALL PLATES, not one per plate. Three loops racing on the same clock is how a
 * menu ends up with plates that settle at visibly different times.
 */
export function animatePlates(root: HTMLElement): PlateAnimator {
  const plates = Array.from(root.querySelectorAll<HTMLElement>(".plate"));
  const state = new Map<HTMLElement, number>();
  const hovered = new WeakSet<HTMLElement>();

  for (const p of plates) {
    state.set(p, 0);
    p.addEventListener("pointerenter", () => hovered.add(p));
    p.addEventListener("pointerleave", () => hovered.delete(p));
  }

  let last = performance.now();
  let raf = 0;

  const step = (now: number) => {
    const dt = Math.min(0.1, (now - last) / 1000);
    last = now;
    const k = 1 - Math.exp(-SMOOTHING * dt);

    for (const p of plates) {
      // ⚠️ `:focus-visible`, NOT `:focus`. A plate clicked with a mouse takes focus, and treating
      // that as "selected" would leave it warm and enlarged after the pointer had gone.
      const hot = hovered.has(p) || p.matches(":focus-visible");
      const cur = state.get(p) ?? 0;
      const next = lerp(cur, hot ? 1 : 0, k);
      state.set(p, next);

      // Nothing to repaint once it has settled. The comparison is against the TARGET so the final
      // frame still lands exactly on 0 or 1 rather than asymptotically near it.
      if (Math.abs(next - (hot ? 1 : 0)) < 0.0005 && Math.abs(next - cur) < 0.0005) {
        state.set(p, hot ? 1 : 0);
      }

      const t = state.get(p) ?? 0;
      const face = p.querySelector<HTMLElement>(".plate__face");
      const label = p.querySelector<HTMLElement>(".plate__label");

      p.style.transform = `scale(${lerp(1, HOT_SCALE_X, t).toFixed(4)}, ${lerp(1, HOT_SCALE_Y, t).toFixed(4)})`;
      p.style.background = mix(REST_EDGE, HOT_EDGE, t);

      if (face) {
        face.style.background = mix(REST_FILL, HOT_FILL, t);
        // ⚠️ THE RIM IS A FRACTION OF THE PLATE, NOT A CONSTANT. `RevivalPlate.EdgeWidth` is
        // documented as exactly that: "EdgeWidth IS A FRACTION OF ITS PLATE, SO IT CANNOT HOLD A
        // CONSTANT". A fixed 1px rim would read as a heavy border on a short plate and vanish on a
        // tall one - and the hover makes this plate 1.518x taller while it is being watched.
        const rim = HOT_EDGE_WIDTH * p.getBoundingClientRect().height * t;
        face.style.inset = `${rim.toFixed(2)}px`;
      }

      if (label) label.style.color = mix(REST_LABEL, HOT_EDGE, t);
    }

    raf = requestAnimationFrame(step);
  };

  raf = requestAnimationFrame(step);
  return { stop: () => cancelAnimationFrame(raf) };
}
